<template>
  <div class="tcla-graph">
    <div class="tcla-panel">
      <div class="tcla-mode-switch">
        <button class="tcla-mode-btn" :class="{ 'tcla-mode-btn--active': mode === 'ego' }" @click="mode = 'ego'">
          🔍 نمای یک مقاله
        </button>
        <button class="tcla-mode-btn" :class="{ 'tcla-mode-btn--active': mode === 'cluster' }" @click="mode = 'cluster'">
          📁 نمای خوشه
        </button>
      </div>

      <!-- Ego network controls -->
      <div v-if="mode === 'ego'" class="tcla-controls">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="نام مقاله را جستجو کن..."
          class="tcla-search-input"
        >
        <div v-if="searchQuery && filteredOptions.length" class="tcla-search-results">
          <div
            v-for="opt in filteredOptions.slice(0, 8)"
            :key="opt.url"
            class="tcla-search-result"
            @click="selectArticle(opt.url)"
          >
            {{ opt.title }}
          </div>
        </div>
      </div>

      <!-- Cluster controls -->
      <div v-if="mode === 'cluster'" class="tcla-controls">
        <select v-model="selectedCluster" class="tcla-select">
          <option value="">— انتخاب خوشه —</option>
          <option v-for="c in topicalClusters" :key="c.name" :value="c.name">{{ c.name }} ({{ c.pageCount }} صفحه)</option>
        </select>
      </div>
    </div>

    <!-- Ego network view -->
    <div v-if="mode === 'ego' && focusNode" class="tcla-panel">
      <h3 class="tcla-panel__title">🕸️ نقشه لینک‌های «{{ focusNode.title }}»</h3>
      <div class="tcla-ego-legend">
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--in"></i> لینک ورودی (این‌ها به مقاله لینک داده‌اند)</span>
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--focus"></i> مقاله انتخاب‌شده</span>
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--out"></i> لینک خروجی (مقاله به این‌ها لینک داده)</span>
      </div>
      <svg :viewBox="'0 0 ' + egoLayout.width + ' ' + egoLayout.height" class="tcla-svg">
        <line v-for="(e, i) in egoLayout.edges" :key="'e'+i"
          :x1="e.x1" :y1="e.y1" :x2="e.x2" :y2="e.y2" class="tcla-edge" />
        <g v-for="n in egoLayout.nodes" :key="n.url" class="tcla-node-group" @click="n.type !== 'focus' && selectArticle(n.url)">
          <circle :cx="n.x" :cy="n.y" :r="n.r" :class="'tcla-node tcla-node--' + n.type" />
          <text :x="n.x" :y="n.y + n.r + 14" class="tcla-label">{{ n.shortTitle }}</text>
        </g>
      </svg>
      <div v-if="egoOverflow.incoming || egoOverflow.outgoing" class="tcla-overflow-note">
        <span v-if="egoOverflow.incoming">+{{ egoOverflow.incoming }} لینک ورودی دیگر</span>
        <span v-if="egoOverflow.outgoing">+{{ egoOverflow.outgoing }} لینک خروجی دیگر</span>
      </div>
    </div>
    <div v-else-if="mode === 'ego'" class="tcla-panel">
      <div class="tcla-empty">یک مقاله را از جستجوی بالا انتخاب کن</div>
    </div>

    <!-- Cluster view -->
    <div v-if="mode === 'cluster' && clusterLayout" class="tcla-panel">
      <h3 class="tcla-panel__title">🕸️ نقشه خوشه «{{ selectedCluster }}»</h3>
      <div class="tcla-ego-legend">
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--hub"></i> Hub</span>
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--spoke"></i> صفحه معمولی</span>
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--orphan"></i> یتیم</span>
        <span class="tcla-legend-item"><i class="tcla-dot tcla-dot--deadend"></i> بن‌بست</span>
      </div>
      <svg :viewBox="'0 0 ' + clusterLayout.width + ' ' + clusterLayout.height" class="tcla-svg">
        <line v-for="(e, i) in clusterLayout.edges" :key="'e'+i"
          :x1="e.x1" :y1="e.y1" :x2="e.x2" :y2="e.y2"
          class="tcla-edge" :class="{ 'tcla-edge--missing': e.missing }" />
        <g v-for="n in clusterLayout.nodes" :key="n.url" class="tcla-node-group" @click="switchToEgo(n.url)">
          <circle :cx="n.x" :cy="n.y" :r="n.r" :class="'tcla-node tcla-node--' + n.type" />
          <text :x="n.x" :y="n.y + n.r + 14" class="tcla-label">{{ n.shortTitle }}</text>
        </g>
      </svg>
    </div>
    <div v-else-if="mode === 'cluster'" class="tcla-panel">
      <div class="tcla-empty">یک خوشه را از بالا انتخاب کن</div>
    </div>
  </div>
</template>

<script>
const MAX_EGO_LINKS = 10; // per side, to keep the graph readable

export default {
  name: 'TCLALinkGraph',
  props: {
    graph: { type: Object, default: () => ({ nodes: {} }) },
    topicalClusters: { type: Array, default: () => [] },
    hubs: { type: Array, default: () => [] }
  },
  data() {
    return {
      mode: 'ego',
      searchQuery: '',
      selectedArticleUrl: null,
      selectedCluster: ''
    };
  },
  computed: {
    allOptions() {
      return Object.values(this.graph.nodes).map(n => ({ url: n.url, title: n.title }));
    },
    filteredOptions() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return [];
      return this.allOptions.filter(o => o.title.toLowerCase().includes(q));
    },
    focusNode() {
      if (!this.selectedArticleUrl) return null;
      return this.graph.nodes[this.selectedArticleUrl] || null;
    },
    egoOverflow() {
      if (!this.focusNode) return { incoming: 0, outgoing: 0 };
      return {
        incoming: Math.max(0, this.focusNode.incomingLinks.length - MAX_EGO_LINKS),
        outgoing: Math.max(0, this.focusNode.outgoingLinks.length - MAX_EGO_LINKS)
      };
    },
    egoLayout() {
      if (!this.focusNode) return { width: 800, height: 400, nodes: [], edges: [] };
      const width = 800;
      const incoming = this.focusNode.incomingLinks.slice(0, MAX_EGO_LINKS);
      const outgoing = this.focusNode.outgoingLinks.slice(0, MAX_EGO_LINKS);
      const rows = Math.max(incoming.length, outgoing.length, 1);
      const height = Math.max(300, rows * 60 + 80);
      const cx = width / 2, cy = height / 2;
      const shortTitle = (t) => (t && t.length > 18 ? t.slice(0, 18) + '…' : (t || ''));

      const nodes = [{ url: this.focusNode.url, x: cx, y: cy, r: 24, type: 'focus', shortTitle: shortTitle(this.focusNode.title) }];
      const edges = [];

      incoming.forEach((link, i) => {
        const srcNode = this.graph.nodes[link.url];
        const y = 60 + i * (height - 100) / Math.max(1, incoming.length - 1 || 1);
        const x = 120;
        nodes.push({ url: link.url, x, y, r: 14, type: 'in', shortTitle: shortTitle(srcNode ? srcNode.title : link.url) });
        edges.push({ x1: x, y1: y, x2: cx, y2: cy });
      });

      outgoing.forEach((link, i) => {
        const tgtNode = this.graph.nodes[link.url];
        const y = 60 + i * (height - 100) / Math.max(1, outgoing.length - 1 || 1);
        const x = width - 120;
        nodes.push({ url: link.url, x, y, r: 14, type: 'out', shortTitle: shortTitle(tgtNode ? tgtNode.title : link.url) });
        edges.push({ x1: cx, y1: cy, x2: x, y2: y });
      });

      return { width, height, nodes, edges };
    },
    clusterLayout() {
      if (!this.selectedCluster) return null;
      const cluster = this.topicalClusters.find(c => c.name === this.selectedCluster);
      if (!cluster) return null;
      const hub = this.hubs.find(h => h.cluster === this.selectedCluster);
      const others = cluster.pages.filter(u => !hub || u !== hub.url);

      const width = 700, height = Math.max(320, 120 + others.length * 8);
      const cx = width / 2, cy = height / 2, radius = Math.min(width, height) / 2 - 70;
      const shortTitle = (t) => (t && t.length > 16 ? t.slice(0, 16) + '…' : (t || ''));

      const nodes = [];
      const edges = [];

      if (hub) {
        const hubNode = this.graph.nodes[hub.url];
        nodes.push({ url: hub.url, x: cx, y: cy, r: 22, type: 'hub', shortTitle: shortTitle(hub.title) });
      }

      others.forEach((url, i) => {
        const node = this.graph.nodes[url];
        if (!node) return;
        const angle = (2 * Math.PI * i) / Math.max(1, others.length);
        const x = cx + radius * Math.cos(angle);
        const y = cy + radius * Math.sin(angle);
        let type = 'spoke';
        if (node.isOrphan) type = 'orphan';
        else if (node.isDeadEnd) type = 'deadend';
        nodes.push({ url, x, y, r: 13, type, shortTitle: shortTitle(node.title) });

        if (hub) {
          const linked = node.outgoingLinks.some(l => l.url === hub.url) ||
            this.graph.nodes[hub.url].outgoingLinks.some(l => l.url === url);
          edges.push({ x1: cx, y1: cy, x2: x, y2: y, missing: !linked });
        }
      });

      return { width, height, nodes, edges };
    }
  },
  methods: {
    selectArticle(url) {
      this.selectedArticleUrl = url;
      this.searchQuery = '';
    },
    switchToEgo(url) {
      this.mode = 'ego';
      this.selectedArticleUrl = url;
    }
  }
};
</script>

<style scoped>
.tcla-panel {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 20px;
  margin-bottom: 16px;
}
.tcla-panel__title { font-size: 0.92rem; font-weight: 700; margin: 0 0 12px 0; }
.tcla-empty { text-align: center; padding: 30px; color: #9ca3af; }

.tcla-mode-switch { display: flex; gap: 8px; margin-bottom: 14px; }
.tcla-mode-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  border-radius: 0.375rem;
  font-size: 0.82rem;
  cursor: pointer;
}
.tcla-mode-btn--active { background: #3b82f6; color: #fff; border-color: #3b82f6; }

.tcla-controls { position: relative; }
.tcla-search-input, .tcla-select {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.85rem;
  font-family: inherit;
}
.tcla-search-results {
  position: absolute;
  top: 100%;
  right: 0;
  left: 0;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  margin-top: 4px;
  z-index: 10;
  max-height: 240px;
  overflow-y: auto;
  box-shadow: 0 4px 10px rgba(0,0,0,0.08);
}
.tcla-search-result {
  padding: 9px 12px;
  font-size: 0.82rem;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
}
.tcla-search-result:hover { background: #f3f4f6; }

.tcla-ego-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 14px;
  font-size: 0.76rem;
  color: #6b7280;
}
.tcla-legend-item { display: inline-flex; align-items: center; gap: 6px; }
.tcla-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; }
.tcla-dot--in { background: #16a34a; }
.tcla-dot--out { background: #3b82f6; }
.tcla-dot--focus { background: #f59e0b; }
.tcla-dot--hub { background: #3b82f6; }
.tcla-dot--spoke { background: #16a34a; }
.tcla-dot--orphan { background: #ef4444; }
.tcla-dot--deadend { background: #9ca3af; }

.tcla-svg { width: 100%; height: auto; }

.tcla-edge { stroke: #d1d5db; stroke-width: 1.5; }
.tcla-edge--missing { stroke: #ef4444; stroke-dasharray: 4 3; }

.tcla-node { stroke: #fff; stroke-width: 2; cursor: pointer; }
.tcla-node--focus { fill: #f59e0b; }
.tcla-node--in { fill: #16a34a; }
.tcla-node--out { fill: #3b82f6; }
.tcla-node--hub { fill: #3b82f6; }
.tcla-node--spoke { fill: #16a34a; }
.tcla-node--orphan { fill: #ef4444; }
.tcla-node--deadend { fill: #9ca3af; }

.tcla-node-group { cursor: pointer; }
.tcla-label { font-size: 10px; fill: #6b7280; text-anchor: middle; }

.tcla-overflow-note {
  margin-top: 10px;
  font-size: 0.76rem;
  color: #9ca3af;
  display: flex;
  gap: 14px;
}
</style>
